import { HttpClient } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, forkJoin, map, Observable, of, shareReplay, Subject, take } from 'rxjs';
import { ConfigurationService } from './configuration.service';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastMessageComponent } from '../shared/components/toast-message/toast-message.component';
import { RestrictionTypes, subscriptionState, TierId,inactiveSubscriptionStates } from '../shared/enums/subscription.enum';
import * as dayjs from 'dayjs';
import { SubscriptionReminderPopupComponent } from '../shared/components/subscription-reminder-popup/subscription-reminder-popup.component';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionManagementService {
  private url: string;
  private maxioUrl: string;

  private dataSubject = new BehaviorSubject<any | null>(null);
  data$ = this.dataSubject.asObservable(); // Track data changes

  getSubscribedFeaturesList = new Subject<any>();
  public getSubscribedFeaturesList$ = this.getSubscribedFeaturesList.asObservable();
  subscriptionData: any = {};

  private featuresSubject = new BehaviorSubject<string[] | null>(null);
  features$ = this.featuresSubject.asObservable(); // Expose feature list

  private addOnFeaturesListSubject = new BehaviorSubject<string[] | null>(null);

  constructor(private http: HttpClient, private configService: ConfigurationService,
    private toastMessage: MatSnackBar,public dialog: MatDialog,private dom: DomSanitizer,) {
    this.url = this.configService.getConfig('auth')?.caretakerUrl;
    this.maxioUrl = this.configService.getConfig('externalServices');
  }

  resetSubscriptionData(): void {
    this.dataSubject.next(null);
  }

  setSubscriptionData(data: any): Promise<void> {
    if (!data) return Promise.resolve(); // Return resolved promise if no data
  
    const handleError = <T>(source: string) => catchError<T, Observable<T>>((error) => {
      this.showToast('error',`Error in ${source}` + error?.error?.error);
      return of(null as T);
    });
  
    const requests = {
      featureResponse: data?.tierId ? this.getSubscribedFeatures(data?.tierId) : of(null),
      addOnsFeatureResponse: this.getSubscribedFeatures(TierId.AddOns),
      tierDetails: data?.siteId ? this.getSiteSubscriptionDetails(data.siteId) : of(null),
      siteBillingAdmins: data?.siteId
        ? this.getSiteBillingAdminDetails(data.siteId).pipe(
            map((response: any) => response?.users?.billingAdmin || response?.users?.facilityManager?.filter((user:any) => user?.isPrimary)),
            catchError((error) => {
              this.showToast('error',error?.error?.error);
              return of(null);
            })
          )
        : of(null),
    };
  
    return new Promise((resolve) => {
      forkJoin(requests)
        .pipe(handleError('Merge Subscription Details'))
        .subscribe(({ featureResponse, addOnsFeatureResponse, tierDetails, siteBillingAdmins }) => {
          const featureList = (featureResponse as any)?.tierFeatures?.length
            ? (featureResponse as any)?.tierFeatures[0].features.split(', ')
            : null;
          
          const addOnsFeatureList = (addOnsFeatureResponse as any)?.tierFeatures?.length
            ? (addOnsFeatureResponse as any)?.tierFeatures[0].features.split(', ')
            : null;
          
          // Add opted addOns to the feature list
          if (data?.addOns?.length) {
            featureList.push(...data.addOns);
          }
          
          // Update subjects only once, after all API responses are received
          this.featuresSubject.next(featureList);
          this.addOnFeaturesListSubject.next(addOnsFeatureList);
          this.dataSubject.next({ ...data, tierDetails, siteBillingAdmins });
  
          resolve(); // Resolve the promise once all async calls complete
        });
    });
  }

  getSiteSubscriptionDetails(siteId: string): Observable<any> {
    return this.http.get(`${this.maxioUrl}maxio/subscription-details?siteId=${siteId}`).pipe(
      catchError((error) => {
        return of(null);
      })
    );
  }

  getSubscriptionData(): Observable<any | null> {
    return this.data$.pipe(
      filter(data => !!data), // Ignore null
      distinctUntilChanged(), // Prevent redundant emissions
      shareReplay(1) // Prevents duplicate emissions for multiple subscribers
    );
  }

  // Toast Message 
  showToast(type: string, message: string) {
    this.toastMessage.openFromComponent(ToastMessageComponent, {
      duration: 5000,
      data: {
        type: type,
        message: message
      },
    });
  }

   // ✅ Get restriction type based on subscription status
  getRestrictionType(isFeatureSubscribed: boolean, subscriptionData: any, feature: string): RestrictionTypes | null {
    const isAddOnFeature = this.isAddonFeature(feature);
    if (!isFeatureSubscribed && subscriptionData?.tierDetails?.status === subscriptionState.active) {
      return isAddOnFeature ? RestrictionTypes.Add_On_Feature : RestrictionTypes.Tier_Upgrade;
    } else if (subscriptionData?.tierDetails?.status === subscriptionState.canceled || subscriptionData?.tierDetails?.status === subscriptionState.onHold) {
      return RestrictionTypes.canceled;
    } else if (subscriptionData?.tierDetails?.status === subscriptionState.expired) {
      return RestrictionTypes.Payment_Due;
    }
    return RestrictionTypes.Tier_Upgrade;
  }

  showSubscriptionReminder(siteId: string) {
    this.getSubscriptionData()
      .pipe(
        filter(data => !!data),
        distinctUntilChanged(),
        take(1)
      )
      .subscribe(data => {
        const tierDetails = data?.tierDetails;
        if (!tierDetails?.billingCycle || siteId !== tierDetails?.siteId || tierDetails?.status != subscriptionState.active) return;

        const billingCycleDate = dayjs(tierDetails?.billingCycle);
        const expiresAt = dayjs(tierDetails?.expiresAt ? tierDetails?.expiresAt : tierDetails?.billingCycle);
        const dayDiff: number = billingCycleDate.diff(dayjs(), 'day');
        const gracePeriod = expiresAt.diff(billingCycleDate, 'day');
        const graceEndDate = expiresAt.isAfter(billingCycleDate) ? expiresAt : billingCycleDate;
        const gracePeriodLeft = graceEndDate.diff(dayjs(), 'day');
        
        // If the billing cycle date is more than 15 days away, or the site has already been reminded, return
        if (dayDiff > 15 || this.isSiteAlreadyReminded(siteId)) return;

        let title = '',htmlContent = '';
        if (dayDiff <= 15 && dayDiff > 0) {
          title = 'Renewal Reminder';
          htmlContent = `Your renewal invoice will arrive in ${dayDiff} ${dayDiff === 1 ? 'day' : 'days'}. Please complete your payment within 30 days to ensure uninterrupted access.`;
          this.openReminderDialog(title, htmlContent, siteId);
        } else if (dayDiff <= 0 && gracePeriod > 0 && gracePeriodLeft >= 0) {
          title = 'Payment Due';
          htmlContent = `Grace period ends in ${gracePeriodLeft} ${gracePeriodLeft === 1 ? 'day' : 'days'}. Please complete your payment by ${graceEndDate.format("MMM DD, YYYY")} to avoid access disruption.`;
          this.openReminderDialog(title, htmlContent, siteId);
        }
      });
  }

  // Helper method to check if the site has already been reminded
  isSiteAlreadyReminded(siteId: string): boolean {
    const existingIds: string[] = JSON.parse(sessionStorage.getItem('remindedSubscriptionSites') || '[]');
    return existingIds.includes(siteId);
  }

  // Open the reminder dialog
  openReminderDialog(title: string, htmlContent: string, siteId: string) {
    const dialogRef = this.dialog.open(SubscriptionReminderPopupComponent, {
      width: '390px',
      panelClass: 'fs-subscription-reminder-popup',
      // Allow the active reminder dialog to participate in standard topmost Escape handling.
      disableClose: false
    });
    dialogRef.componentInstance.title = title;
    dialogRef.componentInstance.htmlContent = this.dom.sanitize(SecurityContext.HTML, htmlContent) || '';
    dialogRef.updatePosition({ top: '190px' });
    this.updateSiteIdToLocalStorage(siteId);
  }
  
  // update the siteIds to local storage to avoid showing the popup again
  updateSiteIdToLocalStorage(siteId: string) {
    // Retrieve existing IDs or initialize an empty array
    const existingIds: string[] = JSON.parse(sessionStorage.getItem('remindedSubscriptionSites') || '[]');
    // Add the ID only if it doesn't already exist
    if (!existingIds.includes(siteId)) {
      existingIds.push(siteId);
      window.parent.postMessage(JSON.stringify({ from: 'subscriptionReminder-Sequencer', 'remindedSubscriptionSites': existingIds }), '*');
      sessionStorage.setItem('remindedSubscriptionSites', JSON.stringify(existingIds));
    }
  }

  //Used to get add on feature list of selected site
  getSubscribedFeatures(tierId: string): Observable<any> {
    return this.http.get(`${this.url}/tier?tierId=${tierId}`).pipe(
      catchError((error) => {
        return of(null);
      })
    );
  }

  //Returns associated roles of selected site
  getSiteBillingAdminDetails(siteId: string): Observable<any> {
    return this.http.get(`${this.url}/sites/@${siteId}/users`).pipe(
      catchError((error) => {
        this.showToast('error','error'+ error?.error?.error);
        return of(null);
      })
    );
  }

     // ✅ Check if a feature exists in the stored list
     hasFeature(feature: string): boolean {
      const featureList = this.featuresSubject.getValue();
      const subscriptionData = this.dataSubject.getValue();
      const status = subscriptionData?.tierDetails?.status;
      return (featureList ? (featureList.includes(feature) || subscriptionData?.addOns?.includes(feature)) : false) &&
         (status == subscriptionState.active || inactiveSubscriptionStates.indexOf(status) === -1);
    }
  
     //Check the feature is present in the addOnFeatures list
    isAddonFeature(feature: string): boolean {
      const addOnFeatureList = this.addOnFeaturesListSubject.getValue();
      return addOnFeatureList ? addOnFeatureList.includes(feature) : false;
    }


}
